"use client";
import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { Info, Check, Store, Loader2, ChevronRight } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/components/shadcn/popover";
import { Badge } from "@/app/components/shadcn/badge";
import { Button } from "@/app/components/shadcn/button";
import { Skeleton } from "@/app/components/shadcn/skeleton";
import useProductDetail from "@/app/lib/features/productDetail/productDetail.hook";
import useStore from "@/app/lib/features/store/store.hook";
import useHasAuthToken from "@/app/hooks/useHasAuthToken";
import { toggleStoreFollow } from "@/app/lib/features/store/store.thunk";
import useModal from "@/app/lib/features/modal/modal.hook";
import { trackSellerFollow } from "@/app/lib/tracking/interactions";

const SellerInfoMobile = () => {
  const { product, isAuthDataSettled } = useProductDetail();
  const { toggleFollow, isFollowLoading } = useStore();
  const hasAuthToken = useHasAuthToken();
  const { createModal } = useModal();
  const [failedBadges, setFailedBadges] = useState<Set<string>>(new Set());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // `is_following` auth'a bağlı ama SSR/ISR HTML'i anonim üretiliyor — auth'lu
  // client fetch dönene kadar takipçide "Takip Et" → "Takip Ediliyor" sıçraması
  // oluyordu. Misafirde değer kesin false olduğundan bekleme yok.
  const isFollowStateKnown = !hasAuthToken || isAuthDataSettled;

  if (!product?.seller) return null;

  const { seller } = product;

  const handleFollowClick = async () => {
    const result = await toggleFollow(seller.id);
    if (toggleStoreFollow.fulfilled.match(result)) {
      if (result.payload.is_following) {
        trackSellerFollow(seller.id);
        createModal("follow-reward", { followData: result.payload });
      }
    }
  };

  return (
    <div className="bg-white border rounded-md p-3 space-y-3">
        {/* Üst satır: logo + isim/takipçi + takip butonu */}
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 rounded-full border overflow-hidden shrink-0 bg-gray-50">
            <FallbackImage
              src={seller.logo_url ?? ""}
              alt={seller.name}
              fill
              className="object-cover"
              sizes="40px"
            />
          </div>

          {/* İsim + takipçi */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {seller.name}
              </span>
              {seller.rating && seller.rating > 0 && (
                <Badge size="sm" className="shrink-0">
                  {seller.rating.toFixed(1)}
                </Badge>
              )}
              {mounted && (seller.info || seller.badges.length > 0) && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="shrink-0">
                      <Info size={13} className="text-muted-foreground" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" className="max-w-xs p-3 space-y-3">
                    {seller.info && (
                      <p className="text-xs text-gray-600">{seller.info}</p>
                    )}
                    {seller.badges.length > 0 && (
                      <div
                        className={`space-y-2 ${seller.info ? "border-t border-gray-100 pt-3" : ""}`}
                      >
                        {seller.badges.map((badge) => (
                          <div key={badge.key} className="flex items-start gap-2">
                            {!failedBadges.has(badge.key) && (
                              <Image
                                src={badge.icon}
                                alt={badge.name}
                                width={16}
                                height={16}
                                className="shrink-0 mt-0.5"
                                onError={() =>
                                  setFailedBadges((prev) =>
                                    new Set(prev).add(badge.key),
                                  )
                                }
                              />
                            )}
                            <div>
                              <p className="text-xs font-semibold text-gray-900">{badge.name}</p>
                              <p className="text-xs text-gray-500">{badge.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {seller.follower_count.toLocaleString("tr-TR")} takipçi
            </p>
          </div>

          {/* Takip butonu */}
          {isFollowStateKnown ? (
            <button
              type="button"
              onClick={handleFollowClick}
              disabled={isFollowLoading}
              className="cursor-pointer shrink-0 flex items-center gap-1 text-xs font-medium disabled:opacity-50 text-main"
            >
              {isFollowLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : seller.is_following ? (
                <>
                  <Check size={14} />
                  Takip Ediliyor
                </>
              ) : (
                <>
                  <Store size={14} />
                  Takip Et
                </>
              )}
            </button>
          ) : (
            <Skeleton className="shrink-0 h-4 w-24 rounded-md" />
          )}
        </div>

        {/* Alt buton: Satıcının Mağazası */}
        <Link href={`/magaza/${seller.slug}`} className="block">
          <Button variant="outline" size="sm" className="w-full gap-1.5">
            <Store size={14} />
            Satıcının Mağazası
            <ChevronRight size={14} className="ml-auto" />
          </Button>
        </Link>
    </div>
  );
};

export default SellerInfoMobile;
